<template>
  <ListItem :to="{ name: 'approvalRequest', params: { id: approvalRequest.id } }">
    <div class="flex-1 space-y-4">
      <h3 class="text-lg font-semibold text-gray-900">
        {{ approvalRequest.title }}
        <div
          :class="statusColors[approvalRequest.status]"
          class="inline-block rounded-md px-2 py-1 text-xs"
        >
          {{ t(`status.${approvalRequest.status}`) }}
        </div>
      </h3>

      <div class="mb-3 text-sm text-gray-600">
        <p>
          <strong>{{ t('requester') }}:</strong> {{ approvalRequest.requester.firstName }}
          {{ approvalRequest.requester.lastName }}
        </p>
        <p>
          <strong>{{ t('created') }}:</strong> {{ formatDate(approvalRequest.createdDate) }}
        </p>
      </div>

      <ApprovalRequestJobData
        :job-name="approvalRequest.jobName"
        :job-data="approvalRequestJobData"
        :result-data="approvalRequestResult"
      />

      <div
        v-if="
          approvalRequest.status !== ApprovalRequestStatus.REJECTED &&
          approvalRequest.status !== ApprovalRequestStatus.APPROVED
        "
        class="flex justify-end gap-4"
      >
        <template
          v-if="isAssignedToMe && approvalRequest.status === ApprovalRequestStatus.REQUESTED"
        >
          <CVButton
            variant="default"
            size="sm"
            :is-loading="approveMutation.isPending.value"
            @click="handleApprove"
          >
            <CheckIcon class="h-4 w-4" />
            {{ t('approve') }}
          </CVButton>

          <CVButton
            variant="danger"
            size="sm"
            :is-loading="rejectMutation.isPending.value"
            @click="showRejectModal = true"
          >
            <XIcon class="h-4 w-4" />
            {{ t('reject') }}
          </CVButton>
        </template>

        <CVButton
          v-else-if="approvalRequest.status === ApprovalRequestStatus.REQUESTED"
          variant="danger"
          size="sm"
          :is-loading="withdrawMutation.isPending.value"
          @click="handleWithdraw"
        >
          {{ t('withdraw') }}
        </CVButton>
      </div>

      <StatusBanner
        v-if="
          approvalRequest.status === ApprovalRequestStatus.REJECTED &&
          approvalRequest.rejectionReason
        "
        variant="danger"
      >
        <div class="min-w-0">
          <p class="text-sm font-medium">{{ t('rejectionReason') }}</p>
          <p class="mt-1 text-sm">{{ approvalRequest.rejectionReason }}</p>
        </div>
      </StatusBanner>

      <!-- Reject Modal -->
      <Modal v-model="showRejectModal" :title="t('rejectRequest')">
        <div class="space-y-4">
          <p class="text-sm text-gray-600">{{ t('rejectRequestDescription') }}</p>
          <Textarea
            v-model:input-text="rejectionReason"
            :label="t('rejectionReason')"
            :placeholder="t('rejectionReasonPlaceholder')"
            required
          />
          <div class="flex justify-end gap-2">
            <CVButton variant="ghost" @click="showRejectModal = false">
              {{ t('cancel') }}
            </CVButton>
            <CVButton
              variant="danger"
              :is-loading="rejectMutation.isPending.value"
              :disabled="!rejectionReason.trim()"
              @click="handleReject"
            >
              {{ t('reject') }}
            </CVButton>
          </div>
        </div>
      </Modal>
    </div>
  </ListItem>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { ApprovalRequestStatus, type ApprovalRequestResponseDto } from '@carvia/ros-client-types';
import {
  useApproveApprovalRequest,
  useRejectApprovalRequest,
  useWithdrawApprovalRequest,
} from '@/queries/use-approval-requests';
import ApprovalRequestJobData from './ApprovalRequestJobData.vue';
import Modal from '@/components/Modal.vue';
import { CheckIcon, XIcon } from 'lucide-vue-next';
import ListItem from '@/components/ListItem.vue';
import StatusBanner from '@/components/StatusBanner.vue';
import Textarea from '@/components/Textarea.vue';
import { formatDate } from '@/hooks/use-formatted-date';

const props = defineProps<{
  approvalRequest: ApprovalRequestResponseDto;
  isAssignedToMe: boolean;
}>();

type ApprovalRequestWithJobPayload = ApprovalRequestResponseDto & {
  jobData?: Record<string, unknown>;
  result?: Record<string, unknown>;
};

const approvalRequestJobData = computed(
  () => (props.approvalRequest as ApprovalRequestWithJobPayload).jobData ?? {},
);
const approvalRequestResult = computed(
  () => (props.approvalRequest as ApprovalRequestWithJobPayload).result,
);

const statusColors = {
  REQUESTED: 'bg-yellow-100 text-yellow-800',
  APPROVED: 'bg-green-100 text-green-800',
  REJECTED: 'bg-red-100 text-red-800',
  WITHDRAWN: 'bg-gray-100 text-gray-800',
};

const { t } = useI18n();

const showRejectModal = ref(false);
const rejectionReason = ref('');

const approveMutation = useApproveApprovalRequest();
const rejectMutation = useRejectApprovalRequest();
const withdrawMutation = useWithdrawApprovalRequest();

const handleApprove = async () => {
  await approveMutation.mutateAsync(props.approvalRequest.id);
};

const handleReject = async () => {
  if (!rejectionReason.value.trim()) return;

  await rejectMutation.mutateAsync({
    id: props.approvalRequest.id,
    rejectionReason: rejectionReason.value.trim(),
  });
  showRejectModal.value = false;
  rejectionReason.value = '';
};

const handleWithdraw = async () => {
  await withdrawMutation.mutateAsync(props.approvalRequest.id);
};
</script>

<i18n lang="json">
{
  "en": {
    "requester": "Requester",
    "created": "Created",
    "tags": "Tags",
    "rejectionReason": "Rejection Reason",
    "approve": "Approve",
    "reject": "Reject",
    "withdraw": "Withdraw",
    "rejectRequest": "Reject Request",
    "rejectRequestDescription": "Please provide a reason for rejecting this approval request.",
    "rejectionReasonPlaceholder": "Enter rejection reason...",
    "cancel": "Cancel",
    "status": {
      "REQUESTED": "Requested",
      "APPROVED": "Approved",
      "REJECTED": "Rejected",
      "WITHDRAWN": "Withdrawn"
    }
  },
  "de": {
    "requester": "Antragsteller",
    "created": "Erstellt",
    "tags": "Tags",
    "rejectionReason": "Ablehnungsgrund",
    "approve": "Genehmigen",
    "reject": "Ablehnen",
    "withdraw": "Zurückziehen",
    "rejectRequest": "Anfrage ablehnen",
    "rejectRequestDescription": "Bitte geben Sie einen Grund für die Ablehnung dieser Genehmigungsanfrage an.",
    "rejectionReasonPlaceholder": "Ablehnungsgrund eingeben...",
    "cancel": "Abbrechen",
    "status": {
      "REQUESTED": "Angefragt",
      "APPROVED": "Genehmigt",
      "REJECTED": "Abgelehnt",
      "WITHDRAWN": "Zurückgezogen"
    }
  },
  "es": {
    "requester": "Solicitante",
    "created": "Creada",
    "tags": "Etiquetas",
    "rejectionReason": "Motivo del rechazo",
    "approve": "Aprobar",
    "reject": "Rechazar",
    "withdraw": "Retirar",
    "rejectRequest": "Rechazar solicitud",
    "rejectRequestDescription": "Indique el motivo del rechazo de esta solicitud de aprobación.",
    "rejectionReasonPlaceholder": "Escriba el motivo del rechazo…",
    "cancel": "Cancelar",
    "status": {
      "REQUESTED": "Solicitada",
      "APPROVED": "Aprobada",
      "REJECTED": "Rechazada",
      "WITHDRAWN": "Retirada"
    }
  }
}
</i18n>
